'use client'

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
} from 'react'

// `Dispatch<SetStateAction<...>>` rather than `(node: ReactNode) => void`: the
// slot setters are `useState` setters in AppShell, and the hooks below need the
// updater form so a cleanup can clear the slot only when its own node is still
// installed. See `useNetworkToolbarNode`.
type NetworkToolbarContextValue = {
  setNetworkToolbar: Dispatch<SetStateAction<ReactNode>>
  setPinnedToolbar: Dispatch<SetStateAction<ReactNode>>
  setRightToolbar: Dispatch<SetStateAction<ReactNode>>
}

export const NetworkToolbarContext = createContext<NetworkToolbarContextValue>({
  setNetworkToolbar: () => undefined,
  setPinnedToolbar: () => undefined,
  setRightToolbar: () => undefined,
})

/**
 * Injects a JSX fragment into AppHeader's "network toolbar" slot. Pass a
 * stable (memoized at the call site) `toolbarNode` so AppHeader does not
 * re-render on every parent render.
 */
export const useNetworkToolbarNode = (toolbarNode: ReactNode): void => {
  const { setNetworkToolbar } = useContext(NetworkToolbarContext)
  useEffect(() => {
    setNetworkToolbar(toolbarNode)
    // Clear only if this instance's node is still the one installed. The
    // cleanup used to set `null` unconditionally, which makes the slot
    // last-writer-wins: when an outgoing page's cleanup runs *after* the
    // incoming page's setup -- ordinary across separate commits, and more
    // likely when the main thread is contended -- it wipes a toolbar that
    // belongs to someone else, and nothing ever puts it back. The search bar
    // and Show All then stay missing from the header until a reload, which is
    // both a researcher-facing bug and why `layout.spec.ts:54` could wait 20s
    // for `#search-bar` and never see it (KBE-770).
    return () =>
      setNetworkToolbar((current: ReactNode) =>
        current === toolbarNode ? null : current,
      )
  }, [toolbarNode, setNetworkToolbar])
}

/**
 * Like `useNetworkToolbarNode`, but renders into a slot that stays visible
 * at every viewport width — unlike the collapsible toolbar slot, which
 * hides below the `lg` breakpoint (KBE-554). Use for controls that convey
 * state the user needs regardless of window size, e.g. the optimization
 * running indicator (KBE-646).
 */
export const usePinnedToolbarNode = (toolbarNode: ReactNode): void => {
  const { setPinnedToolbar } = useContext(NetworkToolbarContext)
  useEffect(() => {
    setPinnedToolbar(toolbarNode)
    // Identity-guarded for the same reason as `useNetworkToolbarNode`.
    return () =>
      setPinnedToolbar((current: ReactNode) =>
        current === toolbarNode ? null : current,
      )
  }, [toolbarNode, setPinnedToolbar])
}

/**
 * Injects a JSX fragment into AppHeader's right-aligned slot, rendered after
 * the flex spacer just before the notification bell. Used to keep a
 * view-mutating control (Show All) far from the search box so it can't be
 * clicked by accident while searching (KBE-866). Stays visible at every
 * viewport width, like the pinned slot.
 */
export const useRightToolbarNode = (toolbarNode: ReactNode): void => {
  const { setRightToolbar } = useContext(NetworkToolbarContext)
  useEffect(() => {
    setRightToolbar(toolbarNode)
    // Identity-guarded for the same reason as `useNetworkToolbarNode`.
    return () =>
      setRightToolbar((current: ReactNode) =>
        current === toolbarNode ? null : current,
      )
  }, [toolbarNode, setRightToolbar])
}
